import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { containerVariants, itemVariants } from '../../lib/animations';
import { useAuth } from '../../contexts/AuthContext';
import { useHtmlBackground } from '../../hooks/useHtmlBackground';
import { api } from '../../services/api';
import { ApiError } from '../../services/apiError';
import {
  CONTAINER_MAX_WIDTH,
  HORIZONTAL_PADDING,
  TYPOGRAPHY,
  BUTTON_HEIGHT,
  SPACING,
  CHECK_EMAIL_ILLUSTRATION,
  CHECK_EMAIL_SPACING,
} from './constants';

import playMomentsLogo from '../../../assets/icons/home-v2/header-playmoments.svg';
import cloudGroup from '../../../assets/check-email-screen/cloud-group.svg';
import backgroundDesign from '../../../assets/check-email-screen/background-design.svg';

const ERROR_MESSAGES: Record<string, string> = {
  EMAIL_RATE_LIMIT: 'We allow 15 emails per hour, please try after sometime.',
  EMAIL_SEND_FAILED: 'Something went wrong sending your email. Please try again in sometime.',
};

const DEFAULT_ERROR = 'Something went wrong, please try again later.';
const SUPPORT_MESSAGE = 'Contact support at ioana@playmoments.app';

function getErrorMessage(error: unknown): { message: string; showSupport: boolean; retriable: boolean } {
  if (error instanceof ApiError && error.code) {
    return {
      message: ERROR_MESSAGES[error.code] || DEFAULT_ERROR,
      showSupport: true,
      retriable: error.retriable,
    };
  }
  if (error instanceof ApiError) {
    return { message: DEFAULT_ERROR, showSupport: true, retriable: error.retriable };
  }
  return { message: DEFAULT_ERROR, showSupport: true, retriable: false };
}

function getCooldownDuration(resendCount: number): number {
  if (resendCount < 2) return 30;
  return 60;
}

export default function LoginEmailSentPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const email = (location.state as { email?: string })?.email;

  const [resendCount, setResendCount] = useState(0);
  const [cooldownSeconds, setCooldownSeconds] = useState(30);
  const [isCooldownActive, setIsCooldownActive] = useState(true);
  const [isResending, setIsResending] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  useHtmlBackground('#DBF7F8');

  useEffect(() => {
    if (!isCooldownActive || cooldownSeconds <= 0) {
      if (cooldownSeconds <= 0) setIsCooldownActive(false);
      return;
    }
    const timer = setInterval(() => {
      setCooldownSeconds((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [isCooldownActive, cooldownSeconds]);

  if (!authLoading && isAuthenticated) {
    navigate('/home', { replace: true });
    return null;
  }

  if (authLoading) {
    return null;
  }

  if (!email) {
    navigate('/login', { replace: true });
    return null;
  }

  const handleResend = async () => {
    if (isCooldownActive || isResending) return;
    setIsResending(true);
    setErrorMessage('');
    try {
      await api.requestMagicLink({ email });
      const newCount = resendCount + 1;
      setResendCount(newCount);
      setCooldownSeconds(getCooldownDuration(newCount));
      setIsCooldownActive(true);
    } catch (error) {
      const { message, showSupport, retriable } = getErrorMessage(error);
      setErrorMessage(showSupport ? `${message} ${SUPPORT_MESSAGE}` : message);
      if (retriable) {
        setIsCooldownActive(false);
        setCooldownSeconds(0);
      }
    } finally {
      setIsResending(false);
    }
  };

  const getButtonText = () => {
    if (isResending) return 'RESENDING...';
    if (isCooldownActive && cooldownSeconds > 0) return `RESEND IN ${cooldownSeconds}S`;
    return 'RESEND EMAIL';
  };

  const isButtonDisabled = isCooldownActive || isResending;

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col items-center overflow-hidden"
      style={{ backgroundColor: '#DBF7F8' }}
    >
      <div
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(to bottom, #01A7C8 0%, rgba(1,167,200,0) 500px)',
        }}
      />

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className={`relative z-10 flex flex-col items-center
          w-full ${CONTAINER_MAX_WIDTH} h-full`}
      >
        {/* Logo */}
        <motion.div variants={itemVariants} className={`${SPACING.logoTop} self-start ${HORIZONTAL_PADDING}`}>
          <img
            src={playMomentsLogo}
            alt="Play Moments"
            className={`${SPACING.logoWidth} h-auto`}
          />
        </motion.div>

        {/* Illustration */}
        <motion.div
          variants={itemVariants}
          className={`relative w-full ${CHECK_EMAIL_SPACING.illustrationTop} ${CHECK_EMAIL_ILLUSTRATION.containerHeight} flex items-center justify-center`}
        >
          <img
            src={backgroundDesign}
            alt=""
            className="absolute left-1/2 -translate-x-1/2 w-screen h-auto"
          />
          <img
            src={cloudGroup}
            alt="Cloud character with envelope"
            className={`relative ${CHECK_EMAIL_ILLUSTRATION.cloudGroupWidth} h-auto`}
          />
        </motion.div>

        {/* Content */}
        <motion.div variants={itemVariants} className={`w-full mt-8 flex flex-col ${CHECK_EMAIL_SPACING.contentGap} ${HORIZONTAL_PADDING}`}>
          <h1
            className={`font-bold ${TYPOGRAPHY.title} leading-tight tracking-[-0.04em] text-center`}
            style={{ color: '#1B2831' }}
          >
            Check your Email
          </h1>

          <p
            className={`${TYPOGRAPHY.description} text-center`}
            style={{ color: '#1B2831' }}
          >
            We&apos;ve sent a magic link to
          </p>

          <div className="flex items-center justify-center h-12">
            <span
              className="text-[18px] font-bold tracking-[-0.04em] text-center"
              style={{ color: '#1B2831' }}
            >
              {email}
            </span>
          </div>

          <p
            className="text-[14px] leading-relaxed"
            style={{ color: '#1B2831' }}
          >
            Tap the link in your email to log in.
            <br />
            The link expires in 24 hours.
          </p>

          {errorMessage && (
            <p className="text-sm text-red-600">{errorMessage}</p>
          )}

          <button
            onClick={handleResend}
            disabled={isButtonDisabled}
            className={`w-full ${BUTTON_HEIGHT} rounded-xl uppercase disabled:cursor-not-allowed ${TYPOGRAPHY.button} font-black tracking-[0.72px]`}
            style={{
              backgroundColor: isButtonDisabled ? '#A9B7BE' : '#1B2831',
              color: '#FFFFFF',
            }}
          >
            {getButtonText()}
          </button>

          <button
            onClick={() => navigate('/login/email')}
            className={`w-full ${BUTTON_HEIGHT} rounded-xl uppercase ${TYPOGRAPHY.button} font-black tracking-[0.72px] ${CHECK_EMAIL_SPACING.secondaryButtonTop}`}
            style={{ backgroundColor: 'transparent', color: '#1B2831' }}
          >
            USE A DIFFERENT EMAIL
          </button>
        </motion.div>
      </motion.div>
    </div>
  );
}
